import { useFacilityContext } from '@aspendental/shared-components-web';
import { Form, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import { Alert, Button, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import { REASON_FOR_VISIT_ID } from '../ReasonForVisit/ReasonForVisit.helpers';
import ContinueButton from '@src/components/ContinueButton';
import { DataLayerStepNames } from '@src/constants';
import { useGeolocation } from '@src/hooks/useGeolocation';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import GetCoordinates from '@src/services/coordinates';
import { dataLayer } from '@src/services/dataLayer';
import { PatientInformationSchema, useAppStore, usePatientInformationFormStore } from '@src/store';
export default function FindLocation() {
  const router = useRouter();
  const {
    locale
  } = router;
  const [zipLookupError, setZipLookupError] = useState(false);
  const [geolocationDisabled, setGeolocationDisabled] = useState(false);
  const [geolocationRequested, setGeolocationRequested] = useState(false);
  const webFacilityDetails = useFacilityContext();
  const {
    t
  } = useTranslation('findLocation');
  const setZipCode = usePatientInformationFormStore(store => store.setZipCode);
  const setGeolocation = usePatientInformationFormStore(store => store.setGeolocation);
  const isMottoFlow = useAppStore(store => store.mottoFlow);
  const isDenturesFlow = useAppStore(store => store.denturesFlow);
  const schema = SchemaBuilder([{
    name: 'zipCode',
    validation: PatientInformationSchema.zipCode
  }]);
  const defaultValues = {
    zipCode: ''
  };
  const form = useHookForm({
    schema,
    defaultValues
  });
  const geolocationService = useGeolocation();
  useEffect(() => {
    if (geolocationService.loading) {
      return;
    }
    if (geolocationService.error) {
      setGeolocationDisabled(true);
      return;
    }
    setGeolocationDisabled(false);
    const {
      latitude,
      longitude
    } = geolocationService;
    setGeolocation(latitude as number, longitude as number);
    dataLayer({
      language_preference: locale,
      brand: isMottoFlow ? 'mottoaligners' : 'aspendental',
      step_name: DataLayerStepNames.findLocation,
      reason_for_visit: isDenturesFlow ? DigitalVisitReasonEnum.Dentures : DigitalVisitReasonEnum.ClearAligners,
      reason_for_visit_id: isDenturesFlow ? REASON_FOR_VISIT_ID[DigitalVisitReasonEnum.Dentures] : REASON_FOR_VISIT_ID[DigitalVisitReasonEnum.ClearAligners],
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      })
    });
  }, [geolocationService, setGeolocation, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name]);
  const handleSubmit = async (fields: FieldValues) => {
    const {
      zipCode
    } = fields;
    try {
      const coords = await GetCoordinates(zipCode);
      const coordinates = coords;
      setZipLookupError(false);
      if (typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number') {
        setGeolocation(coordinates.lat, coordinates.lng);
        setZipCode(zipCode);
        dataLayer({
          language_preference: locale,
          brand: isMottoFlow ? 'mottoaligners' : 'aspendental',
          step_name: DataLayerStepNames.findLocation,
          reason_for_visit: DigitalVisitReasonEnum.ClearAligners,
          reason_for_visit_id: REASON_FOR_VISIT_ID[DigitalVisitReasonEnum.ClearAligners],
          ...(webFacilityDetails?.officeInfo?.code && {
            website_selected_facility_id: webFacilityDetails?.officeInfo?.code
          }),
          ...(webFacilityDetails?.officeInfo?.name && {
            website_selected_facility_name: webFacilityDetails?.officeInfo?.name
          })
        });
      } else {
        setZipLookupError(true);
      }
    } catch (e) {
      console.error(e);
      setZipLookupError(true);
    }
  };
  const handleGeolocationRequested = () => {
    setGeolocationRequested(true);
  };
  return <Stack alignItems="center" data-sentry-element="Stack" data-sentry-component="FindLocation" data-sentry-source-file="FindLocation.tsx">
			<Form form={form as any} onSubmit={handleSubmit} data-sentry-element="Form" data-sentry-source-file="FindLocation.tsx">
				<Grid container spacing={2} maxWidth="26rem" data-sentry-element="Grid" data-sentry-source-file="FindLocation.tsx">
					<Grid item xs={12} textAlign="center" data-sentry-element="Grid" data-sentry-source-file="FindLocation.tsx">
						<Typography sx={{
            marginTop: 18,
            marginBottom: 3
          }} color="primary" variant="header2" data-sentry-element="Typography" data-sentry-source-file="FindLocation.tsx">
							{t('findLocation')}
						</Typography>
					</Grid>

					<Grid item xs={12} textAlign="center" data-sentry-element="Grid" data-sentry-source-file="FindLocation.tsx">
						{/* change needed in translation right here: */}
						<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} required={false} data-sentry-element="TextInput" data-sentry-source-file="FindLocation.tsx" />
					</Grid>

					{zipLookupError && <Grid item xs={12} marginTop={3}>
							<Alert severity="error" variant="flatError">
								<Typography color="text.secondary">{t('reEnterZip')}</Typography>
							</Alert>
						</Grid>}

					<Grid item xs={12} textAlign="center" data-sentry-element="Grid" data-sentry-source-file="FindLocation.tsx">
						<Button sx={{
            margin: 3
          }} variant="tertiaryDefault" color="primary" onClick={handleGeolocationRequested} data-sentry-element="Button" data-sentry-source-file="FindLocation.tsx">
							{t('useCurrent')}
						</Button>
					</Grid>

					{geolocationRequested && geolocationDisabled && <Grid item xs={12} marginTop={3}>
							<Alert severity="error" variant="flatError">
								<Typography color="text.secondary">{t('pleaseEnable')}</Typography>
							</Alert>
						</Grid>}
					<Grid item xs={12} textAlign="center" pb={2} sx={{
          marginTop: 4
        }} data-sentry-element="Grid" data-sentry-source-file="FindLocation.tsx">
						<ContinueButton data-sentry-element="ContinueButton" data-sentry-source-file="FindLocation.tsx" />
					</Grid>
				</Grid>
			</Form>
		</Stack>;
}